import React, { useMemo, useRef, useState } from 'react';

import { Autocomplete } from '@flixbus/honeycomb-react';

import { formatTime } from 'utils/datetime';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';

const COMMON = CREATE_BREAKDOWN_FLOW_TEXT.common;

/**
 * Renders the "Segment time: 09:54AM → 11:53AM" subtitle shown under each
 * option in the picker. Times are formatted in the segment's from-station
 * timezone (falls back to the caller's local zone if the field is missing).
 * Returns an empty string when either timestamp is absent so HC skips the
 * subtitle line for that option.
 *
 * @param {{
 *   fromTimestamp?: string,
 *   toTimestamp?: string,
 *   fromStation?: { timezone?: string },
 * }} option
 * @returns {string}
 */
export const buildSegmentSubtitle = (option) => {
	const { fromTimestamp, toTimestamp, fromStation } = option ?? {};
	if (!fromTimestamp || !toTimestamp) return '';
	const tz = fromStation?.timezone;
	const from = formatTime(fromTimestamp, { timezone: tz });
	const to = formatTime(toTimestamp, { timezone: tz });
	return COMMON.segmentTimeSubtitle(from, to);
};

/**
 * Case-insensitive substring filter over the flat `{id, label}` list of
 * segment options. Matches against the label (which already concatenates
 * from/to station codes and names). Exported for unit tests.
 *
 * @param {Array<{ id: string, label: string }>} options
 * @param {string} inputValue
 * @returns {Array<{ id: string, label: string }>}
 */
export const filterSegmentOptions = (options, inputValue) => {
	const trimmed = (inputValue ?? '').trim().toLowerCase();
	if (!trimmed) return options;
	return options.filter((opt) => (opt.label ?? '').toLowerCase().includes(trimmed));
};

/**
 * Adapts a segment option ({id, label, ...}) to the HC ListBox option shape
 * ({title, value, subtitle?, data}). Kept outside the component so it does
 * not need closure and does not recreate per render.
 */
const toListBoxOption = (opt) => {
	const subtitle = buildSegmentSubtitle(opt);
	const option = { title: opt.label, value: opt.id, data: opt };
	if (subtitle) option.subtitle = subtitle;
	return option;
};

/**
 * Segment picker for Step 1 of the breakdown flow. Backed by HC Autocomplete
 * over the flat `options` list assembled by the parent
 * (`deriveSegmentOptions(assignment)`).
 *
 * `value` (segment id) prefills the combobox input with the matching label so
 * a re-mount / navigate-back keeps the pick visible. Clearing the input
 * entirely also clears the stored id.
 *
 * @param {{
 *   options: Array<{ id: string, label: string }>,
 *   value: string,
 *   onChange: (id: string) => void,
 *   label?: string,
 *   placeholder?: string,
 *   testId?: string,
 * }} props
 */
const SegmentAutocomplete = ({
	options,
	value,
	onChange,
	label = COMMON.segmentLabel,
	placeholder = COMMON.segmentPlaceholder,
	testId,
}) => {
	const initialInputValue = useMemo(() => {
		const match = options.find((o) => o.id === value);
		return match?.label ?? '';
	}, [options, value]);

	const [inputValue, setInputValue] = useState(initialInputValue);
	const inputFieldRef = useRef(null);

	const filteredOptions = useMemo(
		() => filterSegmentOptions(options, inputValue).map(toListBoxOption),
		[options, inputValue]
	);

	const selectedValue = useMemo(() => {
		const match = options.find((o) => o.id === value);
		return match ? [toListBoxOption(match)] : [];
	}, [options, value]);

	const handleInputChange = (event) => {
		const next = event.target.value;
		setInputValue(next);
		if (value && next === '') {
			onChange('');
		}
	};

	const handleChange = (selectedOptions) => {
		const next = Array.isArray(selectedOptions) ? selectedOptions : [];
		if (next.length === 0) {
			onChange('');
			setInputValue('');
			return;
		}
		const picked = next[0];
		onChange(picked.value);
		setInputValue(picked.title);
	};

	const handleReset = () => {
		onChange('');
		setInputValue('');
		requestAnimationFrame(() => inputFieldRef.current?.click());
	};

	return (
		<Autocomplete
			value={selectedValue}
			inputValue={inputValue}
			options={filteredOptions}
			onChange={handleChange}
			comboboxProps={{
				label,
				placeholder,
				onChange: handleInputChange,
				resetProps: { label: COMMON.segmentAutocompleteResetLabel, onClick: handleReset },
				inputFieldRef,
				'data-testid': testId,
			}}
			listBoxProps={{
				label: COMMON.segmentAutocompleteListLabel,
				optionsToDisplay: 5,
			}}
		/>
	);
};

export default SegmentAutocomplete;
